/**
 * useGlobalPresence — Google-Slides-style global presence for Cinqo Ops.
 *
 * Only shows users who are/were online during this session.
 * No full team fetch — the presence channel is the sole source of truth.
 * When someone disconnects they stay visible (greyed) for the session.
 *
 * Usage:
 *   const { team } = useGlobalPresence(user)
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface TeamMember {
  id: string
  email: string
  fullName: string
  avatarUrl: string | null
  online: boolean
}

interface PresencePayload {
  user_id: string
  email: string
  full_name: string
  avatar_url: string | null
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

export function useGlobalPresence(
  user: { id: string; email?: string; user_metadata?: { full_name?: string; avatar_url?: string } } | null,
) {
  const [team, setTeam] = useState<TeamMember[]>([])
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null)
  // Accumulate every user seen this session (keyed by user_id)
  const seenRef = useRef<Map<string, { email: string; fullName: string; avatarUrl: string | null }>>(new Map())

  const syncPresence = useCallback(
    (presenceState: Record<string, PresencePayload[]>) => {
      const onlineIds = new Set<string>()

      // Register every online user we haven't seen yet
      for (const entries of Object.values(presenceState)) {
        for (const entry of entries) {
          onlineIds.add(entry.user_id)
          if (!seenRef.current.has(entry.user_id)) {
            seenRef.current.set(entry.user_id, {
              email: entry.email,
              fullName: entry.full_name,
              avatarUrl: entry.avatar_url,
            })
          }
        }
      }

      // Build team from seen users only
      const members: TeamMember[] = []
      for (const [id, info] of seenRef.current) {
        members.push({
          id,
          email: info.email,
          fullName: info.fullName,
          avatarUrl: info.avatarUrl,
          online: onlineIds.has(id),
        })
      }
      setTeam(members)
    },
    [],
  )

  useEffect(() => {
    if (!user) return

    // Seed self into seen list immediately
    seenRef.current.set(user.id, {
      email: user.email ?? '',
      fullName: user.user_metadata?.full_name ?? user.email ?? '',
      avatarUrl: user.user_metadata?.avatar_url ?? null,
    })
    setTeam([{
      id: user.id,
      email: user.email ?? '',
      fullName: user.user_metadata?.full_name ?? user.email ?? '',
      avatarUrl: user.user_metadata?.avatar_url ?? null,
      online: true,
    }])

    const channel = supabase.channel('global:ops', {
      config: { presence: { key: user.id } },
    })

    channelRef.current = channel

    channel.on('presence', { event: 'sync' }, () => {
      syncPresence(channel.presenceState())
    })

    channel.subscribe(async (status: string) => {
      if (status === 'SUBSCRIBED') {
        await channel.track({
          user_id: user.id,
          email: user.email ?? '',
          full_name: user.user_metadata?.full_name ?? user.email ?? '',
          avatar_url: user.user_metadata?.avatar_url ?? null,
        } satisfies PresencePayload)
      }
    })

    return () => {
      ;supabase.removeChannel(channel)
      channelRef.current = null
    }
  }, [user?.id, syncPresence])

  return { team }
}
